import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import { ReportConfig } from '../components/pdf-export/PDFReportRenderer'

// Re-export types for backward compatibility
export type { ReportConfig as PDFExportOptions }

// Legal landscape dimensions in mm
const LEGAL_WIDTH_MM = 355.6  // 14 inches
const LEGAL_HEIGHT_MM = 215.9 // 8.5 inches
const PAGE_MARGIN_MM = 10     // Margins on each side
const CONTENT_WIDTH_MM = LEGAL_WIDTH_MM - (PAGE_MARGIN_MM * 2)

// html2canvas scale factor for quality
const CANVAS_SCALE = 2

// Fixed width for PDF capture in pixels (14 inches at 96 DPI)
// This ensures consistent capture regardless of browser viewport
const LEGAL_WIDTH_PX = 1344 // 14 * 96

// Compensation for html2canvas text baseline rendering issue (in pixels)
// html2canvas renders text slightly lower than browsers do
const TEXT_BASELINE_OFFSET_PX = -6

interface RowBoundary {
  topPx: number      // Top position in pixels relative to table
  heightPx: number   // Height in pixels
  isHeader: boolean
}

// Type declaration for File System Access API (not in all TypeScript libs)
declare global {
  interface Window {
    showSaveFilePicker?: (options?: {
      suggestedName?: string
      types?: Array<{
        description: string
        accept: Record<string, string[]>
      }>
    }) => Promise<FileSystemFileHandle>
  }
  interface FileSystemFileHandle {
    createWritable(): Promise<FileSystemWritableFileStream>
  }
  interface FileSystemWritableFileStream extends WritableStream {
    write(data: Blob | ArrayBuffer | string): Promise<void>
    close(): Promise<void>
  }
}



export class PDFExportService {
  /**
   * Export a rendered HTML element to PDF using html2canvas
   * This captures the EXACT visual appearance of the preview
   * with proper row-based page breaks
   */
  static async exportFromElement(
    element: HTMLElement,
    config: ReportConfig
  ): Promise<void> {
    const filename = this.generateFilename(config)

    // IMPORTANT: Get file handle FIRST while still in user gesture context
    // The File System Access API requires this to be called synchronously from user action
    const fileHandle = await this.getFileHandle(filename)

    // Create PDF in legal landscape format
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'legal'
    })

    // Set document properties
    pdf.setProperties({
      title: `${config.makeName} ${config.modelName} Market Summary`,
      subject: 'Aircraft Market Summary Report',
      creator: 'IntelliTrack',
      keywords: 'aircraft, market, summary, report'
    })

    // Find the cover page, market overview page, and table sections
    const coverPage = element.querySelector('.ij-cover-page') as HTMLElement
    const marketOverviewPage = element.querySelector('.ij-market-overview-page') as HTMLElement
    const tableSections = element.querySelectorAll('.pdf-preview-container') as NodeListOf<HTMLElement>

    // Capture cover page (full page)
    // The cover page could be either:
    // 1. The standard cover page (only has .ij-cover-page class)
    // 2. The Market Overview page acting as cover (has both .ij-cover-page and .ij-market-overview-page classes)
    if (coverPage) {
      await this.captureCoverPage(pdf, coverPage)
    }

    // Capture market overview page (full page) if present AND it's NOT already the cover page
    // If marketOverviewPage has .ij-cover-page class, it was already captured above
    if (marketOverviewPage && !marketOverviewPage.classList.contains('ij-cover-page')) {
      await this.captureMarketOverviewPage(pdf, marketOverviewPage)
    }

    // Process each table section with row-aware page breaks
    for (let i = 0; i < tableSections.length; i++) {
      const section = tableSections[i]
      if (!section) continue

      await this.captureTableSection(pdf, section)
    }

    // Save the PDF using the pre-obtained file handle or fallback
    await this.savePDF(pdf, filename, fileHandle)
  }

  /**
   * Pre-measure dot leaders from the original DOM before cloning
   * Returns a map of cell index -> array of dot counts for each option line
   */
  private static preMeasureDotLeaders(element: HTMLElement): Map<number, number[]> {
    const dotMeasurements = new Map<number, number[]>()
    const cells = element.querySelectorAll('.pdf-table td')

    cells.forEach((cell, cellIndex) => {
      const optionsList = cell.querySelector('.ij-options-list')
      if (!optionsList) return

      const optionLines = optionsList.querySelectorAll('.ij-option-line')
      const dotCounts: number[] = []

      optionLines.forEach(line => {
        const lineEl = line as HTMLElement
        const nameEl = lineEl.querySelector('.ij-option-name') as HTMLElement
        const dotsEl = lineEl.querySelector('.ij-option-dots') as HTMLElement
        const priceEl = lineEl.querySelector('.ij-option-price') as HTMLElement

        if (nameEl && dotsEl && priceEl) {
          // Get actual rendered widths from the live DOM
          const lineWidth = lineEl.getBoundingClientRect().width
          const nameWidth = nameEl.getBoundingClientRect().width
          const priceWidth = priceEl.getBoundingClientRect().width
          const margins = 8 // 4px margin on each side

          // Calculate available space for dots
          const availableWidth = Math.max(0, lineWidth - nameWidth - priceWidth - margins)

          // Measure actual dot width by checking current rendering
          // The dots element contains 150 dots, measure its content width
          const currentDotsWidth = dotsEl.scrollWidth
          const dotsPerPixel = 150 / (currentDotsWidth || 1)

          // Calculate how many dots fit in available space
          const dotCount = Math.max(3, Math.floor(availableWidth * dotsPerPixel))
          dotCounts.push(dotCount)
        } else {
          dotCounts.push(20) // Fallback
        }
      })

      if (dotCounts.length > 0) {
        dotMeasurements.set(cellIndex, dotCounts)
      }
    })

    return dotMeasurements
  }

  /**
   * Prepare cloned DOM for html2canvas capture
   * - Removes preview-only elements (buttons, UI controls) that shouldn't appear in PDF
   * - Sets fixed width for consistent rendering regardless of viewport
   * - Applies text baseline compensation for html2canvas rendering issues
   * - Replaces dot leaders with exact calculated counts (html2canvas doesn't handle overflow:hidden well)
   */
  private static prepareClonedDOM(clonedDoc: Document, originalElement: Element, dotMeasurements?: Map<number, number[]>): void {
    // Remove preview-only elements (e.g., hide buttons) before capture
    const previewOnlyElements = clonedDoc.querySelectorAll('.pdf-preview-only')
    previewOnlyElements.forEach(el => el.remove())

    // Force the cloned element to render at legal landscape width
    const clonedElement = clonedDoc.body.querySelector('.ij-report-root') ||
                          clonedDoc.body.querySelector('.pdf-preview-container') ||
                          clonedDoc.body.firstElementChild
    if (clonedElement) {
      const el = clonedElement as HTMLElement
      el.style.width = `${LEGAL_WIDTH_PX}px`
      el.style.minWidth = `${LEGAL_WIDTH_PX}px`
      el.style.maxWidth = `${LEGAL_WIDTH_PX}px`
    }

    // Find the corresponding section in the cloned document
    // We need to match by section title since we're processing one section at a time
    const originalTitle = originalElement.querySelector('.pdf-section-header')?.textContent || ''
    let targetSection: Element | null = null

    if (originalTitle) {
      // Find the section with matching title in the cloned document
      // The actual class is pdf-preview-container, not pdf-section
      const clonedSections = clonedDoc.querySelectorAll('.pdf-preview-container')
      for (const section of clonedSections) {
        const sectionTitle = section.querySelector('.pdf-section-header')?.textContent || ''
        if (sectionTitle === originalTitle) {
          targetSection = section
          break
        }
      }
    }

    // Fall back to finding any section if no title match
    if (!targetSection) {
      targetSection = clonedDoc.body.querySelector('.pdf-preview-container') || clonedDoc.body
    }

    // Track cell index for dot measurements lookup - ONLY within this section
    let tdIndex = 0

    // Get cells only from the target section's table
    const cells = targetSection.querySelectorAll('.pdf-table th, .pdf-table td')

    cells.forEach(cell => {
      const el = cell as HTMLElement
      const isTd = el.tagName.toLowerCase() === 'td'
      const currentTdIndex = isTd ? tdIndex++ : -1

      // Skip cells containing options list - they have their own flex layout
      // that would be broken by wrapping in a span
      if (el.querySelector('.ij-options-list')) {
        // For options cells, apply the offset directly to the options container
        const optionsList = el.querySelector('.ij-options-list') as HTMLElement
        if (optionsList) {
          optionsList.style.position = 'relative'
          optionsList.style.top = `${TEXT_BASELINE_OFFSET_PX}px`
          optionsList.style.display = 'flex'
          optionsList.style.flexDirection = 'column'
          optionsList.style.width = '100%'

          // Get pre-measured dot counts for this cell
          const dotCounts = dotMeasurements?.get(currentTdIndex) || []

          // Apply inline styles to each option line for html2canvas compatibility
          const optionLines = optionsList.querySelectorAll('.ij-option-line')
          optionLines.forEach((line, lineIndex) => {
            const lineEl = line as HTMLElement
            const nameEl = lineEl.querySelector('.ij-option-name') as HTMLElement
            const dotsEl = lineEl.querySelector('.ij-option-dots') as HTMLElement
            const priceEl = lineEl.querySelector('.ij-option-price') as HTMLElement

            // Replace dots with pre-measured count (exact fit, no overflow needed)
            if (dotsEl && dotCounts[lineIndex] !== undefined) {
              dotsEl.textContent = '.'.repeat(dotCounts[lineIndex])
            }

            // Apply simple inline layout (no flex-based overflow)
            lineEl.style.display = 'flex'
            lineEl.style.flexDirection = 'row'
            lineEl.style.alignItems = 'baseline'
            lineEl.style.width = '100%'
            lineEl.style.whiteSpace = 'nowrap'

            if (nameEl) {
              nameEl.style.flexShrink = '0'
            }
            if (dotsEl) {
              dotsEl.style.flexShrink = '0' // Fixed width now, no shrinking
              dotsEl.style.margin = '0 4px'
              dotsEl.style.color = '#888888'
              dotsEl.style.whiteSpace = 'nowrap'
              dotsEl.style.overflow = 'visible' // Override the overflow:hidden
            }
            if (priceEl) {
              priceEl.style.flexShrink = '0'
              priceEl.style.textAlign = 'right'
            }
          })
        }
        return
      }

      // Handle relative value breakdown cells - similar flex layout
      if (el.querySelector('.ij-rv-breakdown')) {
        const rvBreakdown = el.querySelector('.ij-rv-breakdown') as HTMLElement
        if (rvBreakdown) {
          rvBreakdown.style.position = 'relative'
          rvBreakdown.style.top = `${TEXT_BASELINE_OFFSET_PX}px`
          rvBreakdown.style.display = 'flex'
          rvBreakdown.style.flexDirection = 'column'
          rvBreakdown.style.width = '100%'

          // Style the total
          const rvTotal = rvBreakdown.querySelector('.ij-rv-total') as HTMLElement
          if (rvTotal) {
            rvTotal.style.textAlign = 'right'
            rvTotal.style.fontWeight = 'bold'
            rvTotal.style.marginBottom = '2px'
          }

          // Style the sections container
          const rvSections = rvBreakdown.querySelector('.ij-rv-sections') as HTMLElement
          if (rvSections) {
            rvSections.style.display = 'flex'
            rvSections.style.flexDirection = 'column'
            rvSections.style.width = '100%'
          }

          // Apply inline styles to each line for html2canvas compatibility
          const rvLines = rvBreakdown.querySelectorAll('.ij-rv-line')
          rvLines.forEach((line) => {
            const lineEl = line as HTMLElement
            const labelEl = lineEl.querySelector('.ij-rv-label') as HTMLElement
            const valueEl = lineEl.querySelector('.ij-rv-value') as HTMLElement

            lineEl.style.display = 'flex'
            lineEl.style.flexDirection = 'row'
            lineEl.style.justifyContent = 'space-between'
            lineEl.style.alignItems = 'baseline'
            lineEl.style.width = '100%'

            if (labelEl) {
              labelEl.style.flexShrink = '0'
              labelEl.style.textAlign = 'left'
            }
            if (valueEl) {
              valueEl.style.flexShrink = '0'
              valueEl.style.textAlign = 'right'
            }
          })
        }
        return
      }

      // Wrap content in a span with relative positioning
      const wrapper = clonedDoc.createElement('span')
      wrapper.style.position = 'relative'
      wrapper.style.top = `${TEXT_BASELINE_OFFSET_PX}px`

      // For header cells (th), use block display to preserve multi-line text with whitespace: pre-line
      // For body cells (td), use inline-block
      const isHeader = el.tagName.toLowerCase() === 'th'
      if (isHeader) {
        wrapper.style.display = 'block'
        wrapper.style.whiteSpace = 'pre-line'
        wrapper.style.lineHeight = '1.2'
      } else {
        wrapper.style.display = 'inline-block'
      }

      // Move all children into the wrapper
      while (el.firstChild) {
        wrapper.appendChild(el.firstChild)
      }
      el.appendChild(wrapper)
    })

    // Apply to legend boxes - wrap content in positioned span like cells
    const legendBoxes = clonedDoc.querySelectorAll('.ij-legend-off-market, .ij-legend-deal-pending')
    legendBoxes.forEach(box => {
      const el = box as HTMLElement
      const wrapper = clonedDoc.createElement('span')
      wrapper.style.position = 'relative'
      wrapper.style.top = `${TEXT_BASELINE_OFFSET_PX}px`
      wrapper.style.display = 'inline-block'
      while (el.firstChild) {
        wrapper.appendChild(el.firstChild)
      }
      el.appendChild(wrapper)
    })
  }

  /**
   * Capture the cover page as a full-page image
   */
  private static async captureCoverPage(pdf: jsPDF, coverPage: HTMLElement): Promise<void> {
    const canvas = await html2canvas(coverPage, {
      scale: CANVAS_SCALE,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false
    })

    const imgData = canvas.toDataURL('image/png')
    pdf.addImage(imgData, 'PNG', 0, 0, LEGAL_WIDTH_MM, LEGAL_HEIGHT_MM)
  }

  /**
   * Capture the Market Overview page as a full-page image
   */
  private static async captureMarketOverviewPage(pdf: jsPDF, marketOverviewPage: HTMLElement): Promise<void> {
    pdf.addPage()

    const canvas = await html2canvas(marketOverviewPage, {
      scale: CANVAS_SCALE,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false
    })

    const imgData = canvas.toDataURL('image/png')
    pdf.addImage(imgData, 'PNG', 0, 0, LEGAL_WIDTH_MM, LEGAL_HEIGHT_MM)
  }

  /**
   * Capture a table section with row-aware page breaks
   * Strategy: Capture the ENTIRE section once (preserving exact visual appearance),
   * then slice at row boundaries for proper page breaks.
   */
  private static async captureTableSection(pdf: jsPDF, section: HTMLElement): Promise<void> {
    const table = section.querySelector('.pdf-table') as HTMLTableElement

    // Pre-measure dot leaders from the live DOM before cloning
    // (html2canvas doesn't handle CSS overflow:hidden properly, so we calculate exact dot counts)
    const dotMeasurements = this.preMeasureDotLeaders(section)

    // DEBUG: Set to true to disable row slicing and render whole table as one image
    // This helps diagnose if vertical alignment issues are from slicing or html2canvas
    const DEBUG_NO_SLICING = false

    if (!table || DEBUG_NO_SLICING) {
      // No table (or debug mode), just capture the whole section on one page
      pdf.addPage()
      const canvas = await html2canvas(section, {
        scale: CANVAS_SCALE,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        windowWidth: LEGAL_WIDTH_PX,
        onclone: (clonedDoc) => this.prepareClonedDOM(clonedDoc, section, dotMeasurements)
      })
      const heightMM = (canvas.height / canvas.width) * CONTENT_WIDTH_MM
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', PAGE_MARGIN_MM, PAGE_MARGIN_MM, CONTENT_WIDTH_MM, heightMM)
      return
    }

    // STEP 1: Measure row boundaries BEFORE capturing
    const sectionRect = section.getBoundingClientRect()
    const tableRect = table.getBoundingClientRect()

    // Content before table (section header, legend, etc.)
    const preTableHeightPx = tableRect.top - sectionRect.top

    // Measure thead
    const thead = table.querySelector('thead') as HTMLTableSectionElement
    const theadRect = thead?.getBoundingClientRect()
    const theadHeightPx = theadRect ? theadRect.height : 0
    const theadTopPx = theadRect ? theadRect.top - sectionRect.top : 0

    // Measure each row
    const tbody = table.querySelector('tbody') as HTMLTableSectionElement
    const rows = tbody ? Array.from(tbody.querySelectorAll('tr')) : []
    const rowBoundaries: RowBoundary[] = rows.map(row => {
      const rowRect = row.getBoundingClientRect()
      return {
        topPx: rowRect.top - sectionRect.top,
        heightPx: rowRect.height,
        isHeader: false
      }
    })

    // STEP 2: Capture the entire section ONCE (fast, pixel-perfect)
    const fullCanvas = await html2canvas(section, {
      scale: CANVAS_SCALE,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
      windowWidth: LEGAL_WIDTH_PX,
      onclone: (clonedDoc) => this.prepareClonedDOM(clonedDoc, section, dotMeasurements)
    })

    // Calculate conversion factors
    const pxToCanvasPx = CANVAS_SCALE // html2canvas scale
    // Convert canvas pixels to mm - use this to convert actual slice heights consistently
    const canvasPxToMM = CONTENT_WIDTH_MM / fullCanvas.width

    // STEP 3: Layout pages with row-aware breaks
    pdf.addPage()
    let currentY = PAGE_MARGIN_MM

    // Add pre-table content (section header, legend) on first page
    if (preTableHeightPx > 0) {
      const { canvas: sliceCanvas, actualHeight } = this.sliceCanvas(fullCanvas, 0, preTableHeightPx * pxToCanvasPx)
      const heightMM = actualHeight * canvasPxToMM
      pdf.addImage(sliceCanvas.toDataURL('image/png'), 'PNG', PAGE_MARGIN_MM, currentY, CONTENT_WIDTH_MM, heightMM)
      currentY += heightMM
    }

    // Add table header - cache the slice for reuse on subsequent pages
    let theadSlice: { canvas: HTMLCanvasElement; heightMM: number } | null = null
    if (theadHeightPx > 0) {
      const { canvas: sliceCanvas, actualHeight } = this.sliceCanvas(fullCanvas, theadTopPx * pxToCanvasPx, theadHeightPx * pxToCanvasPx)
      const heightMM = actualHeight * canvasPxToMM
      theadSlice = { canvas: sliceCanvas, heightMM }
      // Draw black bottom border on header (first page)
      this.drawPageBoundaryBorders(sliceCanvas, false, true)
      pdf.addImage(sliceCanvas.toDataURL('image/png'), 'PNG', PAGE_MARGIN_MM, currentY, CONTENT_WIDTH_MM, heightMM)
      currentY += heightMM
    }

    // Add each row, breaking pages as needed
    let isFirstRowOnPage = true // First row after header needs black top border
    for (let rowIdx = 0; rowIdx < rowBoundaries.length; rowIdx++) {
      const boundary = rowBoundaries[rowIdx]
      // Pre-calculate the row slice to get actual height for page break check
      const { canvas: rowCanvas, actualHeight: rowActualHeight } = this.sliceCanvas(
        fullCanvas,
        boundary.topPx * pxToCanvasPx,
        boundary.heightPx * pxToCanvasPx
      )
      const rowHeightMM = rowActualHeight * canvasPxToMM

      // Check if row fits on current page
      const needsPageBreak = currentY + rowHeightMM > LEGAL_HEIGHT_MM - PAGE_MARGIN_MM

      if (needsPageBreak) {
        // Need a new page
        pdf.addPage()
        currentY = PAGE_MARGIN_MM
        isFirstRowOnPage = true

        // Repeat table header on new page (reuse cached slice)
        if (theadSlice) {
          // Draw black bottom border on header when repeated on new pages
          this.drawPageBoundaryBorders(theadSlice.canvas, false, true)
          pdf.addImage(theadSlice.canvas.toDataURL('image/png'), 'PNG', PAGE_MARGIN_MM, currentY, CONTENT_WIDTH_MM, theadSlice.heightMM)
          currentY += theadSlice.heightMM
        }
      }

      // Determine if this is the last row on the current page (next row would cause page break)
      // or the very last row of the table
      const isLastRowOfTable = rowIdx === rowBoundaries.length - 1
      let isLastRowOnPage = false
      if (!isLastRowOfTable) {
        const nextBoundary = rowBoundaries[rowIdx + 1]
        const { actualHeight: nextRowActualHeight } = this.sliceCanvas(
          fullCanvas,
          nextBoundary.topPx * pxToCanvasPx,
          nextBoundary.heightPx * pxToCanvasPx
        )
        const nextRowHeightMM = nextRowActualHeight * canvasPxToMM
        isLastRowOnPage = (currentY + rowHeightMM + nextRowHeightMM) > (LEGAL_HEIGHT_MM - PAGE_MARGIN_MM)
      }

      // Draw black borders at page boundaries
      // - Black top border on first row after header
      // - Black bottom border on last row before page break or end of table
      this.drawPageBoundaryBorders(rowCanvas, isFirstRowOnPage, isLastRowOnPage || isLastRowOfTable)

      // Add the row
      pdf.addImage(rowCanvas.toDataURL('image/png'), 'PNG', PAGE_MARGIN_MM, currentY, CONTENT_WIDTH_MM, rowHeightMM)
      currentY += rowHeightMM
      isFirstRowOnPage = false
    }
  }

  /**
   * Slice a horizontal strip from a canvas
   * Returns both the canvas and the actual height used (for mm calculation consistency)
   */
  private static sliceCanvas(source: HTMLCanvasElement, startY: number, height: number): { canvas: HTMLCanvasElement; actualHeight: number } {
    const roundedStartY = Math.round(startY)
    const roundedHeight = Math.max(1, Math.round(height))

    const slice = document.createElement('canvas')
    slice.width = source.width
    slice.height = roundedHeight
    const ctx = slice.getContext('2d')
    if (ctx) {
      ctx.drawImage(
        source,
        0, roundedStartY, source.width, roundedHeight,
        0, 0, source.width, roundedHeight
      )
    }
    return { canvas: slice, actualHeight: roundedHeight }
  }

  /**
   * Draw black border lines on a canvas at page boundaries
   * @param canvas - The canvas to modify
   * @param top - Draw black line at top
   * @param bottom - Draw black line at bottom
   */
  private static drawPageBoundaryBorders(canvas: HTMLCanvasElement, top: boolean, bottom: boolean): void {
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.strokeStyle = '#000000'
    ctx.lineWidth = CANVAS_SCALE // 1px at display resolution, scaled for canvas

    if (top) {
      ctx.beginPath()
      ctx.moveTo(0, 0.5)
      ctx.lineTo(canvas.width, 0.5)
      ctx.stroke()
    }

    if (bottom) {
      ctx.beginPath()
      ctx.moveTo(0, canvas.height - 0.5)
      ctx.lineTo(canvas.width, canvas.height - 0.5)
      ctx.stroke()
    }
  }

  /**
   * Generate the PDF filename
   */
  private static generateFilename(config: ReportConfig): string {
    const today = new Date()
    const month = today.getMonth() + 1
    const day = today.getDate()
    const year = today.getFullYear()
    const formattedModelName = `${config.makeName} ${config.modelName}`.replace(/\s+/g, '-')
    return `${formattedModelName}-Market-Summary-${month}-${day}-${year}.pdf`
  }

  /**
   * Get a file handle from the user via the File System Access API
   * MUST be called immediately in response to a user gesture (click)
   * Returns null if API not available, user cancels, or an error occurs
   */
  private static async getFileHandle(suggestedFilename: string): Promise<FileSystemFileHandle | null> {
    console.log('[PDF Export] getFileHandle called with filename:', suggestedFilename)
    console.log('[PDF Export] showSaveFilePicker available:', !!window.showSaveFilePicker)

    if (!window.showSaveFilePicker) {
      console.log('[PDF Export] File System Access API not available')
      return null
    }

    try {
      console.log('[PDF Export] Opening file picker dialog...')
      const fileHandle = await window.showSaveFilePicker({
        suggestedName: suggestedFilename,
        types: [{
          description: 'PDF Document',
          accept: { 'application/pdf': ['.pdf'] }
        }]
      })
      console.log('[PDF Export] File picker returned handle:', fileHandle)
      return fileHandle
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('[PDF Export] User cancelled file picker')
        // Return a special marker to indicate cancellation
        throw error // Re-throw to stop the export process
      }
      console.warn('[PDF Export] File picker failed:', error)
      return null
    }
  }

  /**
   * Save PDF to the provided file handle, or fallback to default download
   */
  private static async savePDF(
    pdf: jsPDF,
    filename: string,
    fileHandle: FileSystemFileHandle | null
  ): Promise<void> {
    if (fileHandle) {
      try {
        const pdfBlob = pdf.output('blob')
        console.log('[PDF Export] PDF blob created, size:', pdfBlob.size)

        const writable = await fileHandle.createWritable()
        await writable.write(pdfBlob)
        await writable.close()
        console.log('[PDF Export] File saved successfully to user-selected location')
        return
      } catch (error) {
        console.warn('[PDF Export] Failed to write to file handle, falling back:', error)
      }
    }

    // Fallback: use default jsPDF save
    console.log('[PDF Export] Using fallback pdf.save()')
    pdf.save(filename)
  }

  /**
   * Legacy method for backward compatibility
   * Uses the new html2canvas approach by creating a hidden render container
   */
  static async exportMarketSummaryToPDF(config: ReportConfig): Promise<void> {
    const filename = this.generateFilename(config)

    // Get file handle FIRST while still in user gesture context
    const fileHandle = await this.getFileHandle(filename)

    // For the legacy method, we need to render the component first
    // This method should be called from a component that can provide the rendered element
    // For now, we'll create a simple fallback that creates a basic PDF

    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'legal'
    })

    const pageWidth = pdf.internal.pageSize.getWidth()
    const pageHeight = pdf.internal.pageSize.getHeight()

    // Cover page
    pdf.setFontSize(18)
    pdf.setFont('helvetica', 'bold')
    const title = `${config.makeName} ${config.modelName} Market Summary`
    const titleWidth = pdf.getTextWidth(title)
    pdf.text(title, (pageWidth - titleWidth) / 2, 30)

    pdf.setFontSize(12)
    pdf.setFont('helvetica', 'normal')
    const preparedFor = `Prepared for: ${config.exportOptions.preparedFor || ''}`
    pdf.text(preparedFor, 20, 50)

    pdf.setFontSize(10)
    pdf.setTextColor(128, 128, 128)
    pdf.text('Note: For full visual export, use the Preview modal download button.', 20, pageHeight - 20)

    // Save the PDF using the pre-obtained file handle or fallback
    await this.savePDF(pdf, filename, fileHandle)
  }
}
